import { useNavigate } from "react-router-dom";
import branches from "../data/branches.json";
import { useScrollReveal } from "../hooks/useScrollReveal";

const BranchesCard = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({
    threshold: 0.3,
  });
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal({
    threshold: 0.1,
  });

  const navigate = useNavigate();

  const gotoLocation = (link: string) => {
    navigate(link);
  };

  return (
    <section className="section-padding bg-[var(--bg-primary)] relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute -top-32 -left-32 w-64 h-64 bg-orange-400/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
            headerVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
            Locations
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-4 gowun-batang">
            Our Branches
          </h2>
          <p className="text-[var(--text-secondary)] max-w-2xl mx-auto text-base md:text-lg">
            Multiple locations across Nagpur to serve our shooting community
          </p>
        </div>

        {/* Branches Grid */}
        <div
          ref={gridRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {branches.map((branch, index) => (
            <div
              key={index}
              className={`card group overflow-hidden hover-lift flex flex-col transition-all duration-700 ${
                gridVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Gradient Header */}
              <div className="h-28 md:h-36 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 relative overflow-hidden">
                {/* Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <svg viewBox="0 0 200 200" className="w-full h-full">
                    <pattern
                      id={`grid-${index}`}
                      width="20"
                      height="20"
                      patternUnits="userSpaceOnUse"
                    >
                      <circle cx="10" cy="10" r="1" fill="white" />
                    </pattern>
                    <rect
                      width="200"
                      height="200"
                      fill={`url(#grid-${index})`}
                    />
                  </svg>
                </div>
                {/* Glow */}
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-xl" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 group-hover:bg-white/25 transition-all duration-500">
                    <svg
                      className="w-6 h-6 md:w-8 md:h-8 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium">
                  Branch {index + 1}
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-5 md:p-6 flex flex-col flex-1">
                <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2 gowun-batang group-hover:text-orange-500 transition-colors duration-300">
                  {branch.location}
                </h3>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed mb-6 flex-1">
                  {branch.address}
                </p>
                <a
                  href={branch?.link}
                  target="_blank"
                  className="w-full py-2.5 px-4 rounded-xl border border-[var(--border-primary)] text-[var(--text-primary)] font-medium text-sm hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-all duration-300 flex items-center justify-center gap-2 group/btn"
                >
                  <svg
                    className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                    />
                  </svg>
                  View on Map
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BranchesCard;
